import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  approveKycService,
  fetchAllKycService,
  fetchSingleKycService,
  rejectKycService,
} from "../../apis/kyc";
import { cleanObject } from "../../helpers";

interface KycState {
  allKyc: any;
  singleKyc: any;
  loading: boolean;
  error: string | null;
  approving: boolean;
  rejecting: boolean;
}

const initialState: KycState = {
  allKyc: [],
  singleKyc: null,
  loading: false,
  error: null,
  approving: false,
  rejecting: false,
};

// Fetch all KYC entries
export const fetchAllKyc = createAsyncThunk(
  "kyc/fetchAll",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetchAllKycService(cleanObject(payload));
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch KYC records"
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  }
);

// Fetch single KYC
export const fetchSingleKyc = createAsyncThunk(
  "kyc/fetchSingle",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetchSingleKycService(id);
      return response.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch KYC details"
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  }
);

// Approve KYC
export const approveKyc = createAsyncThunk(
  "kyc/approve",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await approveKycService(payload);
      toast.success("KYC approved successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Approval failed");
      return rejectWithValue(
        error.response?.data?.message ?? "Approval failed"
      );
    }
  }
);
// Approve KYC
export const rejectKyc = createAsyncThunk(
  "kyc/reject",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await rejectKycService(payload);
      toast.success("KYC rejected successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Rejection failed");
      return rejectWithValue(
        error.response?.data?.message ?? "Rejection failed"
      );
    }
  }
);

const kycSlice = createSlice({
  name: "kyc",
  initialState,
  reducers: {
    resetSingleKyc: (state) => {
      state.singleKyc = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchAllKyc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllKyc.fulfilled, (state, action) => {
        state.loading = false;
        state.allKyc = action.payload;
      })
      .addCase(fetchAllKyc.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single
      .addCase(fetchSingleKyc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSingleKyc.fulfilled, (state, action) => {
        state.loading = false;
        state.singleKyc = action.payload;
      })
      .addCase(fetchSingleKyc.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Approve
      .addCase(approveKyc.pending, (state) => {
        state.approving = true;
        state.error = null;
      })
      .addCase(approveKyc.fulfilled, (state, action) => {
        state.approving = false;
      })
      .addCase(approveKyc.rejected, (state, action: PayloadAction<any>) => {
        state.approving = false;
        state.error = action.payload;
      })

      // Reject
      .addCase(rejectKyc.pending, (state) => {
        state.rejecting = true;
        state.error = null;
      })
      .addCase(rejectKyc.fulfilled, (state, action) => {
        state.rejecting = false;
      })
      .addCase(rejectKyc.rejected, (state, action: PayloadAction<any>) => {
        state.rejecting = false;
        state.error = action.payload;
      });
  },
});

export const { resetSingleKyc } = kycSlice.actions;
export default kycSlice.reducer;
